import React, { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { MessageSquare, Users, Search, Eye, Shield, Lock, Hash, KeyRound } from 'lucide-react';

// Super-admin passcode is read from the runtime env (window-injected) or falls back to a sensible default.
// Note: this is a *soft* gate (audit prompt), not a security boundary — backend already enforces super_admin role.
const PASSCODE_KEY = 'chat_monitor_unlocked_until';
const UNLOCK_TTL_MS = 15 * 60 * 1000; // 15 minutes
const EXPECTED_PASSCODE = (typeof window !== 'undefined' && window.__CHAT_MONITOR_PASSCODE__)
  || '4242';

export default function ChatMonitorPage() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [msgDialog, setMsgDialog] = useState({ open: false, convoId: '', title: '', messages: [], loading: false });

  // Passcode gate
  const [unlocked, setUnlocked] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [passcodeError, setPasscodeError] = useState('');
  const [viewMode, setViewMode] = useState('all'); // all | direct | group

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(PASSCODE_KEY);
      const until = raw ? parseInt(raw, 10) : 0;
      if (until && Date.now() < until) {
        setUnlocked(true);
        fetchConversations();
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleUnlock() {
    if (passcode.trim() === EXPECTED_PASSCODE) {
      const until = Date.now() + UNLOCK_TTL_MS;
      try { sessionStorage.setItem(PASSCODE_KEY, String(until)); } catch {}
      setUnlocked(true);
      setPasscodeError('');
      setPasscode('');
      fetchConversations();
    } else {
      setPasscodeError('Invalid passcode. Try again.');
      setPasscode('');
    }
  }

  function handleLock() {
    try { sessionStorage.removeItem(PASSCODE_KEY); } catch {}
    setUnlocked(false);
    setConversations([]);
    setMsgDialog({ open: false, convoId: '', title: '', messages: [], loading: false });
  }

  async function fetchConversations() {
    setLoading(true);
    try {
      const res = await apiClient.get('/security/team-chat-data?days=30');
      setConversations(res.data?.conversations || []);
    } catch {
      toast.error('Failed to load chat data');
    } finally {
      setLoading(false);
    }
  }

  async function viewMessages(convo) {
    const title = convo.type === 'direct'
      ? `DM: ${convo.name || (convo.participants || []).map(p => p.name).join(' & ')}`
      : `#${convo.name || 'Channel'}`;
    setMsgDialog({ open: true, convoId: convo.id, title, messages: [], loading: true });
    try {
      const res = await apiClient.get(`/security/team-chat-messages/${convo.id}`);
      setMsgDialog(prev => ({ ...prev, messages: res.data?.messages || [], loading: false }));
    } catch {
      setMsgDialog(prev => ({ ...prev, loading: false }));
      toast.error('Failed to load messages');
    }
  }

  function getConvoLabel(convo) {
    return convo.name || (convo.type === 'direct' ? 'Direct Message' : 'Group Chat');
  }

  const filtered = conversations.filter(c => {
    if (viewMode === 'direct' && c.type !== 'direct') return false;
    if (viewMode === 'group' && c.type === 'direct') return false;
    if (!search) return true;
    const q = search.toLowerCase();
    const label = getConvoLabel(c).toLowerCase();
    return label.includes(q);
  });

  const dmCount = conversations.filter(c => c.type === 'direct').length;
  const groupCount = conversations.filter(c => c.type !== 'direct').length;
  const totalMsgs = conversations.reduce((s, c) => s + (c.message_count || 0), 0);

  // Passcode gate — soft audit prompt; backend already enforces super_admin role
  if (!unlocked) {
    return (
      <div className="max-w-md mx-auto mt-16" data-testid="chat-monitor-locked">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                <KeyRound className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle>Chat Monitoring — Locked</CardTitle>
                <CardDescription>
                  Enter the Super Admin passcode to view all team conversations.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              type="password"
              autoFocus
              placeholder="Passcode"
              value={passcode}
              onChange={e => setPasscode(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleUnlock(); }}
              data-testid="chat-monitor-passcode-input"
            />
            {passcodeError && <p className="text-sm text-red-600" data-testid="chat-monitor-passcode-error">{passcodeError}</p>}
            <Button className="w-full" onClick={handleUnlock} data-testid="chat-monitor-unlock-btn">
              <Lock className="h-4 w-4 mr-1.5" /> Unlock
            </Button>
            <p className="text-xs text-muted-foreground">
              Unlocks for 15 minutes. All access is server-side audited.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="chat-monitor-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Chat Monitoring</h1>
            <p className="text-muted-foreground text-sm">View all team conversations — Super Admin only (last 30 days)</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleLock} data-testid="chat-monitor-lock-btn">
          <Lock className="h-4 w-4 mr-1.5" /> Lock
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4 text-center">
            <MessageSquare className="h-6 w-6 mx-auto mb-2 text-blue-500" />
            <p className="text-3xl font-bold">{totalMsgs}</p>
            <p className="text-xs text-muted-foreground mt-1">Total Messages</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4 text-center">
            <Lock className="h-6 w-6 mx-auto mb-2 text-amber-500" />
            <p className="text-3xl font-bold">{dmCount}</p>
            <p className="text-xs text-muted-foreground mt-1">Private DMs</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4 text-center">
            <Hash className="h-6 w-6 mx-auto mb-2 text-emerald-500" />
            <p className="text-3xl font-bold">{groupCount}</p>
            <p className="text-xs text-muted-foreground mt-1">Group Channels</p>
          </CardContent>
        </Card>
      </div>

      {/* Search + filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search conversations..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="chat-search" />
        </div>
        <div className="flex items-center gap-1 rounded-md border bg-card p-1">
          {['all', 'direct', 'group'].map(mode => (
            <button
              key={mode}
              type="button"
              onClick={() => setViewMode(mode)}
              className={`px-3 py-1 text-xs font-medium rounded ${viewMode === mode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
              data-testid={`chat-filter-${mode}`}
            >
              {mode === 'all' ? 'All' : mode === 'direct' ? 'DMs' : 'Groups'}
            </button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={fetchConversations} data-testid="chat-refresh-btn">Refresh</Button>
      </div>

      {/* Conversations Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-semibold mb-1">No Conversations Found</h3>
              <p className="text-muted-foreground text-sm">No team chat data in the last 30 days.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Conversation</TableHead>
                  <TableHead>Participants</TableHead>
                  <TableHead className="text-center">Messages</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((convo, i) => (
                  <TableRow key={convo.id || i} data-testid={`convo-row-${i}`}>
                    <TableCell>
                      {convo.type === 'direct' ? (
                        <Badge variant="outline" className="gap-1 text-xs"><Lock className="h-3 w-3" /> DM</Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1 text-xs"><Hash className="h-3 w-3" /> Group</Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{getConvoLabel(convo)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm">{(convo.participants || []).length}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={convo.message_count > 50 ? 'bg-blue-500/10 text-blue-600' : 'bg-muted text-foreground'}>
                        {convo.message_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {convo.updated_at ? new Date(convo.updated_at).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => viewMessages(convo)} data-testid={`view-msgs-${i}`}>
                        <Eye className="h-4 w-4 mr-1" /> View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Message Dialog */}
      <Dialog open={msgDialog.open} onOpenChange={o => { if (!o) setMsgDialog(prev => ({ ...prev, open: false })); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="chat-messages-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-500" />
              {msgDialog.title}
            </DialogTitle>
            <DialogDescription>Conversation messages — read-only</DialogDescription>
          </DialogHeader>
          {msgDialog.loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : msgDialog.messages.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">No messages in this conversation.</div>
          ) : (
            <ScrollArea className="flex-1 max-h-[60vh] pr-3">
              <div className="space-y-3 py-2">
                {msgDialog.messages.map((msg, i) => (
                  <div key={msg.id || i} className="flex gap-3">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold">
                        {(msg.sender_name || 'U').charAt(0)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{msg.sender_name || 'Unknown'}</span>
                        <span className="text-[10px] text-muted-foreground">
                          {msg.created_at ? new Date(msg.created_at).toLocaleString('en-AE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                      <p className="text-sm mt-0.5 whitespace-pre-wrap">{msg.content || msg.message || ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
